import {ApiClient} from '@hconnect/apiclient'
import {useQuery} from '@tanstack/react-query'
import {Moment} from 'moment-timezone'

import {getSchedulePath} from '../api'
import type {Schedule} from '../api'

export type MomentRange = [start: Moment, end: Moment]

export const SCHEDULE_QUERY_KEY = 'schedule'

interface FetchCurrentScheduleParams {
  plantId: string
  range: MomentRange
  signal?: AbortSignal
}

export const getCurrentScheduleQueryKey = (
  plantId: string,
  [startOfPlan, endOfPlan]: MomentRange
) =>
  [
    SCHEDULE_QUERY_KEY,
    {plantCode: plantId, start: startOfPlan.toISOString(), end: endOfPlan.toISOString()}
  ] as const

export const fetchCurrentSchedule = async ({
  plantId,
  range: [startOfPlan, endOfPlan],
  signal
}: FetchCurrentScheduleParams): Promise<Schedule> => {
  const path = getSchedulePath()
  const response = await ApiClient.axiosInstance.get<Schedule>(path, {
    params: {
      plantCode: plantId,
      start: startOfPlan.toISOString(),
      end: endOfPlan.toISOString()
    },
    signal
  })
  return response.data
}

interface UseCurrentScheduleQueryParams {
  plantId: string
  range: MomentRange
  enabled?: boolean
  throwOnError?: boolean
}

export const useCurrentScheduleQuery = <TSelect = Schedule>({
  plantId,
  range,
  enabled = true,
  throwOnError = true,
  select
}: UseCurrentScheduleQueryParams & {
  select?: (data: Schedule) => TSelect
}) => {
  return useQuery<Schedule, unknown, TSelect>({
    queryKey: getCurrentScheduleQueryKey(plantId, range),
    queryFn: ({signal}) => fetchCurrentSchedule({plantId, range, signal}),
    throwOnError,
    enabled,
    ...(enabled ? {} : {gcTime: 0}),
    ...(select ? {select} : {})
  })
}
